/**
 * One date filter for a toolbar: a select-looking button over a panel with
 * the quick ranges (Today, Yesterday, Last 24 hours … All time) on the left
 * and a custom single day or from / to range on the right. Days start at
 * midnight in the zone the page shows times in (local or UTC), so "Today"
 * means the same day the table's timestamps say. A screen scoped to a period
 * (AI usage's month) passes the quick ranges that make sense, a name for
 * "all", and the first and last day the custom fields may pick.
 */

import { useId, useState } from 'react';
import { CalendarDays, Check, ChevronDown } from 'lucide-react';

import { dateLabel, defaultFrom, PRESETS, todayIn, type DateFilterValue, type DatePreset } from './dateFilterValue';
import { usePopover } from './usePopover';

export { dateBounds, dateLabel, type DateFilterValue, type DatePreset } from './dateFilterValue';

type CustomMode = 'day' | 'range';

/** The custom fields' draft, apart from what is applied until Apply. */
function useCustomDates(last: string, min: string | undefined) {
  const [mode, setMode] = useState<CustomMode>('day');
  const [day, setDay] = useState(last);
  const [from, setFrom] = useState(defaultFrom(last, min));
  const [to, setTo] = useState(last);

  /** Start the custom fields from what is applied now. */
  function startFrom(value: DateFilterValue) {
    if (value.kind === 'day') {
      setMode('day');
      setDay(value.day);
    } else if (value.kind === 'range') {
      setMode('range');
      setFrom(value.from);
      setTo(value.to);
    } else {
      // The bounds may have moved (another month): start inside them.
      setDay(last);
      setFrom(defaultFrom(last, min));
      setTo(last);
    }
  }

  return { mode, setMode, day, setDay, from, setFrom, to, setTo, startFrom };
}

type CustomDates = ReturnType<typeof useCustomDates>;

function PresetList({
  value,
  presets,
  allLabel,
  onPick,
}: {
  value: DateFilterValue;
  presets: DatePreset[];
  allLabel: string;
  onPick: (next: DateFilterValue) => void;
}) {
  return (
    <div className="date-presets" role="group" aria-label="Quick ranges">
      {PRESETS.filter((p) => presets.includes(p.key)).map((p) => {
        const active = value.kind === 'preset' && value.preset === p.key;
        return (
          <button
            key={p.key}
            type="button"
            className={`dropdown-item${active ? ' is-active' : ''}`}
            aria-pressed={active}
            onClick={() => onPick({ kind: 'preset', preset: p.key })}
          >
            <span>{p.key === 'all' ? allLabel : p.label}</span>
            {active && <Check aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

function CustomFields({ custom, min, last }: { custom: CustomDates; min?: string; last: string }) {
  const { mode, day, from, to } = custom;
  if (mode === 'day') {
    return (
      <label className="field">
        <span className="label">Day</span>
        <input type="date" value={day} min={min} max={last} onChange={(e) => custom.setDay(e.target.value)} required />
      </label>
    );
  }
  return (
    <>
      <label className="field">
        <span className="label">From</span>
        <input type="date" value={from} min={min} max={to || last} onChange={(e) => custom.setFrom(e.target.value)} required />
      </label>
      <label className="field">
        <span className="label">To</span>
        <input type="date" value={to} min={from || min} max={last} onChange={(e) => custom.setTo(e.target.value)} required />
      </label>
    </>
  );
}

function CustomForm({
  custom,
  min,
  last,
  utc,
  onPick,
}: {
  custom: CustomDates;
  min?: string;
  last: string;
  utc: boolean;
  onPick: (next: DateFilterValue) => void;
}) {
  const { mode, day, from, to } = custom;
  const rangeValid = Boolean(from && to && from <= to);
  return (
    <form
      className="filter-popover-custom"
      onSubmit={(e) => {
        e.preventDefault();
        if (mode === 'day' && day) onPick({ kind: 'day', day });
        if (mode === 'range' && rangeValid) onPick({ kind: 'range', from, to });
      }}
    >
      <div className="segmented" role="radiogroup" aria-label="Custom date">
        <button type="button" role="radio" aria-checked={mode === 'day'} onClick={() => custom.setMode('day')}>
          Single day
        </button>
        <button type="button" role="radio" aria-checked={mode === 'range'} onClick={() => custom.setMode('range')}>
          Date range
        </button>
      </div>
      <CustomFields custom={custom} min={min} last={last} />
      <p className="hint filter-popover-note">Days run midnight to midnight, {utc ? 'UTC' : 'local time'}.</p>
      <button type="submit" className="btn btn-primary btn-sm" disabled={mode === 'day' ? !day : !rangeValid}>
        Apply
      </button>
    </form>
  );
}

export function DateFilter({
  value,
  onChange,
  utc,
  label = 'Date',
  presets = PRESETS.map((p) => p.key),
  allLabel = 'All time',
  min,
  max,
}: {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
  /** Read days in UTC rather than local time. */
  utc: boolean;
  label?: string;
  /** The quick ranges offered, in this order. */
  presets?: DatePreset[];
  /** What the 'all' quick range is called. */
  allLabel?: string;
  /** The first and last day (YYYY-MM-DD) the custom fields may pick; the last defaults to today. */
  min?: string;
  max?: string;
}) {
  const popover = usePopover();
  const panelId = useId();
  const last = max ?? todayIn(utc);
  const custom = useCustomDates(last, min);
  const summary = dateLabel(value, allLabel);

  function open() {
    if (!popover.open) custom.startFrom(value);
    popover.toggle();
  }

  function pick(next: DateFilterValue) {
    onChange(next);
    popover.close();
  }

  return (
    <>
      <button
        ref={popover.button}
        type="button"
        className="multi-select date-filter has-lead"
        aria-label={`${label}: ${summary}`}
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-controls={popover.open ? panelId : undefined}
        onClick={open}
      >
        <CalendarDays className="select-lead-icon" aria-hidden="true" />
        <span className="multi-select-value">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {popover.open && (
        <div
          ref={popover.panel}
          id={panelId}
          className="user-dropdown filter-popover date-filter-panel"
          role="dialog"
          aria-label={label}
          style={popover.style}
        >
          <PresetList value={value} presets={presets} allLabel={allLabel} onPick={pick} />
          <CustomForm custom={custom} min={min} last={last} utc={utc} onPick={pick} />
        </div>
      )}
    </>
  );
}
